import { useEffect, useSyncExternalStore } from 'react';
import { apiClient } from '../lib/apiClient';

/**
 * The school's state in Admin Center Archive Mode, as AI Recording's server sees it:
 * - mode 'readOnly' (Grace period): watching only; 'blocked' (Archived or later): no access.
 *   SuperAdmin always gets 'full' (as in ACV).
 * - legalHold: nothing can be deleted permanently (every role).
 * The server enforces all of it; this only makes the screen match. It is read when the app
 * starts, when ACV announces a change (`acv-archive-access`), when the server refuses something
 * for this reason, and every few minutes.
 */
export interface ArchiveAccess {
  mode: 'full' | 'readOnly' | 'blocked';
  status: string | null;
  legalHold: boolean;
}

const REFRESH_MS = 5 * 60 * 1000;
/** ACV's announcement is a hint; a burst of them asks once. */
const HINT_GAP_MS = 2000;
let lastHint = 0;

let state: ArchiveAccess = { mode: 'full', status: null, legalHold: false };
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;

export const refreshArchiveAccess = (): Promise<void> => {
  if (inflight) return inflight;
  inflight = apiClient.getArchiveAccess()
    .then(({ data }) => {
      if (!data) return;
      const next: ArchiveAccess = { mode: data.mode, status: data.status ?? null, legalHold: !!data.legalHold };
      if (next.mode !== state.mode || next.status !== state.status || next.legalHold !== state.legalHold) {
        state = next;
        listeners.forEach((notify) => notify());
      }
    })
    .catch(() => { /* keep the last known state */ })
    .finally(() => { inflight = null; });
  return inflight;
};

if (typeof window !== 'undefined') {
  // ACV's announcement is only a hint to re-check; the server's answer decides.
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type !== 'acv-archive-access' || Date.now() - lastHint < HINT_GAP_MS) return;
    lastHint = Date.now();
    void refreshArchiveAccess();
  });
  window.addEventListener('airec-archive-refusal', () => void refreshArchiveAccess());
}

const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => { listeners.delete(notify); };
};

export const useArchiveAccess = (): ArchiveAccess => useSyncExternalStore(subscribe, () => state);

/** Mounted once (App) for the signed-in user: read again when the user changes, then every few minutes. */
export const useArchiveAccessPolling = (userEmail: string | null | undefined) => {
  useEffect(() => {
    if (!userEmail) return;
    void refreshArchiveAccess();
    const timer = window.setInterval(() => void refreshArchiveAccess(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [userEmail]);
};

/** True while changes are not allowed (read-only; blocked shows its own screen). */
export const useReadOnly = () => useArchiveAccess().mode !== 'full';
